import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Alert,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import moment from 'moment';
import MockDataIndicator from '../../../../Components/Common/MockDataIndicator';
import { detailedTransactions } from '../../../../mockData/financeData';
import TransactionDetailModal from './TransactionDetailModal';

// Format currency
const formatCurrency = (amount, currency = 'AED') => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

// Transaction type colors and labels
const getTransactionTypeConfig = (type) => {
  const config = {
    TOPUP: { color: 'success', label: 'Wallet Topup' },
    TICKET_PAYMENT: { color: 'primary', label: 'Ticket Payment' },
    PARTNER_PAYMENT: { color: 'warning', label: 'Partner Payment' },
    COMMISSION_PAYMENT: { color: 'info', label: 'Commission Payment' },
    REFUND: { color: 'error', label: 'Refund' },
  };
  return config[type] || { color: 'default', label: type };
};

// Expanded Row Component - Shows detailed breakdown
const TransactionDetailRow = ({ transaction }) => {
  const isTicketPayment = transaction.type === 'TICKET_PAYMENT';
  const isTopup = transaction.type === 'TOPUP';
  const isPartnerPayment = transaction.type === 'PARTNER_PAYMENT';
  const isCommissionPayment = transaction.type === 'COMMISSION_PAYMENT';

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
        Transaction Details
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
        {/* Common Details */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Transaction ID
          </Typography>
          <Typography variant="body2">{transaction.id}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            From
          </Typography>
          <Typography variant="body2">{transaction.from.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            ({transaction.from.type})
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            To
          </Typography>
          <Typography variant="body2">{transaction.to.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            ({transaction.to.type})
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Chip label={transaction.status} color="success" size="small" />
        </Box>

        {/* Topup Specific */}
        {isTopup && transaction.paymentMethod && (
          <>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Payment Method
              </Typography>
              <Typography variant="body2">{transaction.paymentMethod}</Typography>
            </Box>

            {transaction.gatewayFee > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Gateway Fee
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {formatCurrency(transaction.gatewayFee)}
                </Typography>
              </Box>
            )}

            {transaction.gatewayFee > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Net Amount
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  {formatCurrency(transaction.netAmount)}
                </Typography>
              </Box>
            )}

            {transaction.reference && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reference
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  {transaction.reference}
                </Typography>
              </Box>
            )}

            {transaction.bankName && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Bank
                  </Typography>
                  <Typography variant="body2">{transaction.bankName}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reference Number
                  </Typography>
                  <Typography variant="body2">{transaction.referenceNumber}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Approved By
                  </Typography>
                  <Typography variant="body2">{transaction.approvedBy}</Typography>
                </Box>
              </>
            )}
          </>
        )}

        {/* Ticket Payment Specific */}
        {isTicketPayment && (
          <>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ticket Number
              </Typography>
              <Typography variant="body2">{transaction.ticketNumber}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Service
              </Typography>
              <Typography variant="body2">{transaction.serviceName}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Service Partner
              </Typography>
              <Typography variant="body2">{transaction.servicePartner.name}</Typography>
            </Box>

            {transaction.channelPartner && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Channel Partner
                </Typography>
                <Typography variant="body2">{transaction.channelPartner.name}</Typography>
              </Box>
            )}
          </>
        )}

        {/* Balance Tracking */}
        {transaction.openingBalance !== undefined && (
          <>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Opening Balance
              </Typography>
              <Typography variant="body2">{formatCurrency(transaction.openingBalance)}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Closing Balance
              </Typography>
              <Typography variant="body2">{formatCurrency(transaction.closingBalance)}</Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Three-Tier Payment Flow Breakdown */}
      {isTicketPayment && transaction.grossProfit !== undefined && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Payment Flow Breakdown
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Customer Charge:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(transaction.customerCharge)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Service Partner Cost:</Typography>
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                - {formatCurrency(transaction.partnerCost)}
              </Typography>
            </Box>

            <Box
              sx={{ height: '1px', backgroundColor: '#e0e0e0', my: 1 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Gross Profit:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {formatCurrency(transaction.grossProfit)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Channel Partner Commission:</Typography>
              <Typography variant="body2" sx={{ color: 'warning.main' }}>
                - {formatCurrency(transaction.channelCommission)}
              </Typography>
            </Box>

            <Box
              sx={{ height: '1px', backgroundColor: '#e0e0e0', my: 1 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                Net Profit:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'primary.main' }}
              >
                {formatCurrency(transaction.netProfit)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Commission Payment Details */}
      {isCommissionPayment && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Commission Details
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Ticket:</Typography>
              <Typography variant="body2">{transaction.ticketNumber}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Service:</Typography>
              <Typography variant="body2">{transaction.serviceName}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Gross Profit:</Typography>
              <Typography variant="body2">{formatCurrency(transaction.grossProfit)}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Commission Rate:</Typography>
              <Typography variant="body2">{transaction.commissionRate}%</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Commission Amount:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {formatCurrency(transaction.amount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Transaction Row Component
const TransactionRow = ({ transaction, onDetailClick }) => {
  const [open, setOpen] = useState(false);
  const typeConfig = getTransactionTypeConfig(transaction.type);

  const handleRowClick = (e) => {
    // If clicking on expand icon, toggle detail expansion
    if (e.currentTarget === e.target || e.target.closest('[role="button"]')) {
      setOpen(!open);
    } else {
      // Otherwise, open the modal
      onDetailClick(transaction);
    }
  };

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={handleRowClick}>
        <TableCell onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
          <IconButton size="small">
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Typography variant="body2">
            {moment(transaction.date).format('DD MMM YYYY')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {moment(transaction.date).format('HH:mm')}
          </Typography>
        </TableCell>

        <TableCell>
          <Chip label={typeConfig.label} color={typeConfig.color} size="small" />
        </TableCell>

        <TableCell>
          <Typography variant="body2">{transaction.from.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {transaction.from.type}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2">{transaction.to.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {transaction.to.type}
          </Typography>
        </TableCell>

        <TableCell align="right">
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {formatCurrency(transaction.amount)}
          </Typography>
        </TableCell>

        <TableCell>
          <Chip label={transaction.status} color="success" size="small" />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TransactionDetailRow transaction={transaction} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const EnhancedTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isMockData, setIsMockData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    console.log('=== Transaction History Component Mounted ===');
    console.log('Fetching transactions from API...');

    const fetchTransactions = async () => {
      try {
        const token = localStorage.getItem('authToken');
        console.log('Auth token found:', !!token);
        console.log('Calling API: GET /v1/transactions');

        const response = await axios.get('/v1/transactions', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        console.log('API Response received:', {
          status: response.status,
          hasSuccess: !!response.data?.success,
          dataType: typeof response.data?.data,
          isArray: Array.isArray(response.data?.data),
          dataLength: Array.isArray(response.data?.data) ? response.data.data.length : 0,
        });

        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          // Real data from API
          console.log(`✅ SUCCESS: Loaded ${response.data.data.length} real transactions from API`);
          setTransactions(response.data.data);
          setFilteredTransactions(response.data.data);
          setIsMockData(false);
        } else {
          // Fall back to mock data if API doesn't return expected format
          console.warn('⚠️ API response not in expected format, falling back to mock data');
          console.warn('Response structure:', response.data);
          setTransactions(detailedTransactions);
          setFilteredTransactions(detailedTransactions);
          setIsMockData(true);
        }
      } catch (error) {
        // Fall back to mock data if API call fails
        console.error('❌ API Error:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          errorData: error.response?.data,
        });
        console.log('Falling back to mock data...');
        setTransactions(detailedTransactions);
        setFilteredTransactions(detailedTransactions);
        setIsMockData(true);
      }
    };

    fetchTransactions();
  }, []);

  useEffect(() => {
    let filtered = transactions;

    // Type filter
    if (typeFilter !== 'ALL') {
      filtered = filtered.filter((txn) => txn.type === typeFilter);
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (txn) =>
          txn.id.toLowerCase().includes(searchLower) ||
          txn.from.name.toLowerCase().includes(searchLower) ||
          txn.to.name.toLowerCase().includes(searchLower) ||
          (txn.reference && txn.reference.toLowerCase().includes(searchLower)) ||
          (txn.ticketNumber && txn.ticketNumber.toLowerCase().includes(searchLower))
      );
    }

    setFilteredTransactions(filtered);
    setPage(0);
  }, [transactions, searchTerm, typeFilter]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Transaction History
        </Typography>
        <MockDataIndicator isMock={isMockData} />
      </Box>

      {isMockData && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Mock Data Mode:</strong> Displaying sample transactions. Backend integration pending.
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search transactions..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, minWidth: '250px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: '200px' }}>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Transaction Type"
                startAdornment={
                  <InputAdornment position="start">
                    <FilterIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="TOPUP">Wallet Topup</MenuItem>
                <MenuItem value="TICKET_PAYMENT">Ticket Payment</MenuItem>
                <MenuItem value="PARTNER_PAYMENT">Partner Payment</MenuItem>
                <MenuItem value="COMMISSION_PAYMENT">Commission Payment</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((transaction) => (
                  <TransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    onDetailClick={(txn) => {
                      setSelectedTransaction(txn);
                      setModalOpen(true);
                    }}
                  />
                ))}

              {filteredTransactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No transactions found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredTransactions.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Transaction Detail Modal */}
      <TransactionDetailModal
        open={modalOpen}
        transaction={selectedTransaction}
        onClose={() => {
          setModalOpen(false);
          setSelectedTransaction(null);
        }}
      />
    </Box>
  );
};

export default EnhancedTransactions;
